import { Component, Vue } from 'vue-property-decorator'
import { gcd, getKeys } from '~/lib/utils/utils'

import {
  FILTER_AMOUNT,
  FILTER_APPLICANT_COUNT, FILTER_CONF_SCORE,
  FILTER_DEADLINE, FILTER_PAYMENT,
  FILTER_REQ_TYPE
} from '~/lib/utils/filters'
import { formatToUSDCurrency } from '~/lib/utils/format'
import { Store } from '~/types/store'

const filterList = {
  [FILTER_DEADLINE]: {
    iconName: 'clock',
    filterBar: 'DeadlineFilterBar',
    title: 'Filter by time until deadline',
    isProFeature: false,
    selectionOptions: [
      'Less than 1 day',
      '1 - 7 days',
      '1 - 4 weeks',
      'More than 4 weeks'
    ]
  },
  [FILTER_AMOUNT]: {
    iconName: 'dollar-sign',
    filterBar: 'RangeFilterBar',
    maskScheme: '{$ } N',
    title: 'Filter by scholarship amount',
    isProFeature: false
  },
  [FILTER_REQ_TYPE]: {
    iconName: 'double-check',
    iconStroke: true,
    filterBar: 'RequirementFilterBar',
    title: 'Filter by requirement types',
    isProFeature: false,
    description: 'Select requirements you want to exclude.',
    selectionOptions: [
      'Essay',
      'Link',
      'Document',
      'Survey',
      'Photo',
      'Question',
      'Video',
      'Special eligibility',
      'Goal',
      'No requirements'
    ]
  },
  [FILTER_APPLICANT_COUNT]: {
    iconName: 'group',
    filterBar: 'RangeFilterBar',
    maskScheme: 'N {\\applic\\ants}',
    title: 'Filter by number of applicants',
    isProFeature: true,
    proReferer: 'applicant_count',
    tooltipInitiator: 'Applicants filter'
  },
  [FILTER_CONF_SCORE]: {
    iconName: 'shield-check',
    filterBar: 'ScoreFilterBar',
    title: 'Filter by credibility score',
    description: 'We score each scholarship based on past awards and partnership with our providers.',
    isProFeature: true,
    proReferer: 'credibility',
    tooltipInitiator: 'Credibility filter',
    selectionOptions: [
      'Low score (1% - 35%)',
      'Medium score (36% - 69%)',
      'High score (70% - 99%)',
      'Verified'
    ]
  },
  [FILTER_PAYMENT]: {
    iconName: 'credit-card',
    filterBar: 'PaymentFilterBar',
    title: 'Filter by payout method',
    isProFeature: true,
    proReferer: 'payment',
    tooltipInitiator: 'Payment filter',
    selectionOptions: {
      1: 'College',
      2: 'Student',
      3: 'Non-monetary awards',
      4: 'Not Stated'
    }
  }
}

type FilterFilterBar = typeof FILTER_DEADLINE | typeof FILTER_AMOUNT | typeof FILTER_REQ_TYPE | typeof FILTER_APPLICANT_COUNT | typeof FILTER_CONF_SCORE | typeof FILTER_PAYMENT

const rangeInitValues = (list: number[]) => {
  const min: number = list.length ? Math.min.apply(null, list) : 0
  const max: number = list.length ? Math.max.apply(null, list) || 1 : 0
  if (min === max) { return { min: 0, max: 1, interval: 1 } }
  return { min, max, interval: gcd(min || 1, max || 1) }
}

const labels = {
  [FILTER_AMOUNT]: ({ possibleMin, possibleMax }: { possibleMin: number, possibleMax: number }) => {
    return `Amount ${formatToUSDCurrency(possibleMin)} to ${formatToUSDCurrency(possibleMax)}`
  },
  [FILTER_APPLICANT_COUNT]: ({ possibleMin, possibleMax }: { possibleMin: number, possibleMax: number }, isActive: boolean) => {
    return isActive ? `Applicants ${possibleMin} - ${possibleMax}` : 'Applicant number'
  },
  [FILTER_CONF_SCORE]: (filterData: Store.Filter.Set, isActive: boolean) => {
    return isActive ? `Credibility score (${filterData.selected.length})` : 'Credibility score'
  },
  [FILTER_DEADLINE]: (filterData: Store.Filter.Val, isActive: boolean) => {
    if (!filterData.selected) { return 'Time until deadline' }
    return isActive ? filterList[FILTER_DEADLINE].selectionOptions[filterData.selected] : 'Time until deadline'
  },
  [FILTER_PAYMENT]: (filterData: Store.Filter.Set, isActive: boolean) => {
    return isActive ? `Payment (${filterData.selected.length})` : 'Payment'
  },
  [FILTER_REQ_TYPE]: (filterData: Store.Filter.Set, isActive: boolean) => {
    return isActive ? `Requirement type (${filterData.selected.length})` : 'Requirement type'
  }
}

@Component
export default class FilterList extends Vue {
  get tabName(): Store.FilterTab {
    return this.$store.state.route.params.tabName
  }

  filterRepresentationList = getKeys(filterList)

  get getFilterOptions() {
    return (filterName: FilterFilterBar) => {
      return filterList[filterName]
    }
  }

  get rangeFilterInitValues() {
    const list = this.$accessor.tabList.tabList
    const filterNames = [FILTER_AMOUNT, FILTER_APPLICANT_COUNT]

    const propByFilters = {
      [filterNames[0]]: 'amount',
      [filterNames[1]]: 'applicationCount'
    }

    return (filterName: typeof FILTER_AMOUNT | typeof FILTER_APPLICANT_COUNT) => {
      if (!filterNames.includes(filterName)) { return }
      // @ts-ignore
      const range = rangeInitValues(list.map((item: Scholarship.General) => Number(item[propByFilters[filterName]])))
      return { possibleMin: range.min, possibleMax: range.max, interval: range.interval }
    }
  }

  get filterLabel() {
    return (filterName: FilterFilterBar) => {
      const isActive = this.$store.getters['filters/appliedFilterNames'].includes(filterName)
      const value = this.$store.getters['filters/getFilter']({ tabName: this.tabName, filterName })

      if ([FILTER_AMOUNT, FILTER_APPLICANT_COUNT].includes(filterName)) {
        filterName = filterName as typeof FILTER_AMOUNT | typeof FILTER_APPLICANT_COUNT

        // @ts-ignore
        return labels[filterName](
          isActive
            // @ts-ignore
            ? { possibleMin: value.min, possibleMax: value.max, interval: value.interval }
            : this.rangeFilterInitValues(filterName), isActive
        )
      }

      // @ts-ignore
      return labels[filterName]({ ...value, items: this.getFilterOptions(filterName).selectionOptions }, isActive)
    }
  }
}
